/**
 * Structured logging utility for VelloPlay
 * Provides consistent log formatting with context and metadata
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  component?: string;
  action?: string;
  userId?: string;
  tournamentId?: string;
  arenaId?: string;
  [key: string]: unknown;
}

interface StructuredLog {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: LogContext;
  error?: {
    name: string;
    message: string;
    stack?: string;
  };
  duration?: number;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

// Set minimum log level (can be configured via env)
const MIN_LOG_LEVEL: LogLevel = 
  (import.meta.env.VITE_LOG_LEVEL as LogLevel) || 
  (import.meta.env.DEV ? 'debug' : 'info');

function shouldLog(level: LogLevel): boolean {
  return LOG_LEVELS[level] >= LOG_LEVELS[MIN_LOG_LEVEL];
}

function formatLog(log: StructuredLog): string {
  const parts = [
    `[${log.timestamp}]`,
    `[${log.level.toUpperCase()}]`,
    log.context?.component ? `[${log.context.component}]` : '',
    log.context?.action ? `(${log.context.action})` : '',
    log.message,
  ].filter(Boolean);

  return parts.join(' ');
}

function createLog(
  level: LogLevel,
  message: string,
  context?: LogContext,
  error?: Error
): StructuredLog {
  return {
    timestamp: new Date().toISOString(),
    level,
    message,
    context,
    error: error ? {
      name: error.name,
      message: error.message,
      stack: error.stack,
    } : undefined,
  };
}

export const logger = {
  debug(message: string, context?: LogContext): void {
    if (!shouldLog('debug')) return;
    const log = createLog('debug', message, context);
    console.debug(formatLog(log), context || '');
  },

  info(message: string, context?: LogContext): void {
    if (!shouldLog('info')) return;
    const log = createLog('info', message, context);
    console.info(formatLog(log), context || '');
  },

  warn(message: string, context?: LogContext): void {
    if (!shouldLog('warn')) return;
    const log = createLog('warn', message, context);
    console.warn(formatLog(log), context || '');
  },

  error(message: string, error?: Error, context?: LogContext): void {
    if (!shouldLog('error')) return;
    const log = createLog('error', message, context, error);
    console.error(formatLog(log), { ...context, error: log.error });
  },

  /**
   * Time an async operation and log its duration
   */
  async time<T>(
    label: string,
    operation: () => Promise<T>,
    context?: LogContext
  ): Promise<T> {
    const start = performance.now();
    try {
      const result = await operation();
      const duration = Math.round(performance.now() - start);
      this.info(`${label} completed`, { ...context, duration: `${duration}ms` });
      return result;
    } catch (error) {
      const duration = Math.round(performance.now() - start);
      this.error(`${label} failed`, error as Error, { ...context, duration: `${duration}ms` });
      throw error;
    }
  },

  /**
   * Create a scoped logger with preset context
   */
  scope(defaultContext: LogContext) {
    return {
      debug: (message: string, context?: LogContext) =>
        logger.debug(message, { ...defaultContext, ...context }),
      info: (message: string, context?: LogContext) =>
        logger.info(message, { ...defaultContext, ...context }),
      warn: (message: string, context?: LogContext) =>
        logger.warn(message, { ...defaultContext, ...context }),
      error: (message: string, error?: Error, context?: LogContext) =>
        logger.error(message, error, { ...defaultContext, ...context }),
      time: <T>(label: string, operation: () => Promise<T>, context?: LogContext) =>
        logger.time(label, operation, { ...defaultContext, ...context }),
    };
  },
};

export type { LogContext, StructuredLog };
